import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { db } from "./firebase";
import {
  addDoc, collection, doc, onSnapshot, orderBy,
  query, serverTimestamp, updateDoc, runTransaction
} from "firebase/firestore";
import MyBabbleGames from "./MyBabbleGames";
import CelebrationOverlay from "./components/CelebrationOverlay";
import { useSoftSounds } from "./hooks/useSoftSounds";
import BabbleTileBuddy from "./components/BabbleTileBuddy";

/** ---------- Constants & helpers ---------- */

const isOcc = (ch) => ch && ch !== "."; // occupied cell?

const SIZE = 15;

// Bonus layout (TW/TL/DW/DL)
const BONUS = (() => {
  const b = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  const TW = [[0,0],[0,7],[0,14],[7,0],[7,14],[14,0],[14,7],[14,14]];
  const DW = [[1,1],[2,2],[3,3],[4,4],[10,10],[11,11],[12,12],[13,13],[1,13],[2,12],[3,11],[4,10],[10,4],[11,3],[12,2],[13,1],[7,7]];
  const TL = [[1,5],[1,9],[5,1],[5,5],[5,9],[5,13],[9,1],[9,5],[9,9],[9,13],[13,5],[13,9]];
  const DL = [[0,3],[0,11],[2,6],[2,8],[3,0],[3,7],[3,14],[6,2],[6,6],[6,8],[6,12],
              [7,3],[7,11],[8,2],[8,6],[8,8],[8,12],[11,0],[11,7],[11,14],[12,6],[12,8],[14,3],[14,11]];
  TW.forEach(([r,c]) => b[r][c] = "TW");
  DW.forEach(([r,c]) => b[r][c] = "DW");
  TL.forEach(([r,c]) => b[r][c] = "TL");
  DL.forEach(([r,c]) => b[r][c] = "DL");
  return b;
})();

const BONUS_LABEL = {
  TW: "TRIPLE WORD",
  DW: "DOUBLE WORD",
  TL: "TRIPLE LETTER",
  DL: "DOUBLE LETTER",
};

const POINTS = {
  A:1,B:3,C:3,D:2,E:1,F:4,G:2,H:4,I:1,J:8,K:5,L:1,M:3,N:1,O:1,P:3,Q:10,R:1,S:1,T:1,U:1,V:4,W:4,X:8,Y:4,Z:10
};

// 100-ish English-like bag
const BAG = "AAAAAAAAABBCCDDDDEEEEEEEEEEEEFFGGGHHIIIIIIIIIJKLLLMMMNNNNNNOOOOOOOOPPQRRRRRRSSSSTTTTTTUUUUVVWWXYYZ";

function rackPoints(rack) {
  return (rack || []).reduce((sum, ch) => sum + (POINTS[ch] || 0), 0);
}

const BOARD_FRAME_STYLE = {
  padding: "48px 32px 32px",
  borderRadius: 36,
  backgroundColor: "#114329",
  backgroundImage: `
    radial-gradient(circle at 25% 20%, rgba(255,255,255,0.08), transparent 55%),
    radial-gradient(circle at 70% 30%, rgba(255,255,255,0.05), transparent 60%),
    linear-gradient(145deg, #0f3a25, #1a5a36)
  `,
  boxShadow: "0 40px 70px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)",
  border: "2px solid rgba(255,255,255,0.05)",
  position: "relative",
  overflow: "hidden"
};
const BOARD_SURFACE_STYLE = {
  borderRadius: 28,
  padding: "18px 18px 28px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))",
  boxShadow: "inset 0 6px 24px rgba(0,0,0,0.45)",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(0.5px)",
  display:"flex",
  justifyContent:"center"
};
const BOARD_WRAPPER_STYLE = {
  overflowX:"auto",
  display:"flex",
  justifyContent:"center"
};

function drawTiles(bag, rack, n) {
  const arr = bag.split("");
  const take = Math.min(n, arr.length);
  for (let i = 0; i < take; i++) {
    const idx = Math.floor(Math.random() * arr.length);
    rack.push(arr.splice(idx, 1)[0]);
  }
  return { bag: arr.join(""), rack };
}

/** Board stored as 15 strings of length 15 ('.' = empty) */
function newBoardRows() {
  return Array.from({ length: SIZE }, () => ".".repeat(SIZE));
}
function rowsToMatrix(rows) {
  return rows.map(row => row.split(""));
}
function setCell(rows, r, c, letter) {
  const ch = letter && letter.length ? letter[0] : ".";
  const row = rows[r];
  return [
    ...rows.slice(0, r),
    row.slice(0, c) + ch + row.slice(c + 1),
    ...rows.slice(r + 1),
  ];
}

/** placements = [{r,c,letter,rackIndex}] ; boardBefore = matrix 15x15 */
function scoreMove(boardBefore, placements) {
  if (placements.length === 0) return { ok:false, reason:"No tiles", score:0 };

  // Is this the very first move (no letters on the board)?
  let hasAnyTile = false;
  outer: for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      if (isOcc(boardBefore[r][c])) { hasAnyTile = true; break outer; }
    }
  }

  // Apply placements into a working copy
  const board = boardBefore.map(row => row.slice());
  placements.forEach(p => { board[p.r][p.c] = p.letter; });

  // All placements must be in a single row or column
  const rows = new Set(placements.map(p => p.r));
  const cols = new Set(placements.map(p => p.c));
  const singleRow = rows.size === 1;
  const singleCol = cols.size === 1;
  if (!singleRow && !singleCol) {
    return { ok:false, reason:"Place tiles in a single row or column.", score:0 };
  }

  // First move must cover the center star (DW)
  if (!hasAnyTile) {
    const center = { r: Math.floor(SIZE/2), c: Math.floor(SIZE/2) };
    const hitsCenter = placements.some(p => p.r === center.r && p.c === center.c);
    if (!hitsCenter) {
      return { ok:false, reason:"First word must cover the center DW square.", score:0 };
    }
  } else {
    // Non-first moves must touch at least one existing tile
    const touchesExisting = placements.some(p => (
      (p.r > 0            && isOcc(boardBefore[p.r-1][p.c])) ||
      (p.r+1 < SIZE       && isOcc(boardBefore[p.r+1][p.c])) ||
      (p.c > 0            && isOcc(boardBefore[p.r][p.c-1])) ||
      (p.c+1 < SIZE       && isOcc(boardBefore[p.r][p.c+1]))
    ));
    if (!touchesExisting) {
      return { ok:false, reason:"Your move must connect to existing tiles.", score:0 };
    }
  }

  // Helper: build a contiguous word through a starting cell along a direction
  const buildWord = (r, c, dr, dc) => {
    // back up to word start
    while (r - dr >= 0 && r - dr < SIZE && c - dc >= 0 && c - dc < SIZE && isOcc(board[r - dr][c - dc])) {
      r -= dr; c -= dc;
    }
    const cells = [];
    while (r >= 0 && r < SIZE && c >= 0 && c < SIZE && isOcc(board[r][c])) {
      cells.push({ r, c, letter: board[r][c] });
      r += dr; c += dc;
    }
    return cells;
  };

  // Helper: score one word according to Scrabble rules
  const placedSet = new Set(placements.map(p => `${p.r},${p.c}`));
  const scoreWord = (cells) => {
    let sum = 0;
    let wordMult = 1;
    for (const cell of cells) {
      const isNew = placedSet.has(`${cell.r},${cell.c}`);
      let v = POINTS[cell.letter] || 0;
      if (isNew) {
        const bonus = BONUS[cell.r][cell.c];
        if      (bonus === "DL") v *= 2;
        else if (bonus === "TL") v *= 3;
        else if (bonus === "DW") wordMult *= 2;
        else if (bonus === "TW") wordMult *= 3;
      }
      sum += v;
    }
    return sum * wordMult;
  };

  // Build the main word along the line of play and ensure it's contiguous
  const [r0, c0] = [placements[0].r, placements[0].c];
  const mainCells = singleRow
    ? buildWord(r0, c0, 0, 1)
    : buildWord(r0, c0, 1, 0);

  // Ensure the main word actually covers every placed tile (no gaps)
  const coversAllPlacements = placements.every(p =>
    mainCells.some(cell => cell.r === p.r && cell.c === p.c)
  );
  if (!coversAllPlacements) {
    return { ok:false, reason:"Tiles must form one contiguous word (no gaps).", score:0 };
  }

  // Score main word
  let total = scoreWord(mainCells);

  // Score cross-words made by each new tile (orthogonal direction)
  for (const p of placements) {
    const cross = singleRow
      ? buildWord(p.r, p.c, 1, 0) // vertical
      : buildWord(p.r, p.c, 0, 1); // horizontal
    if (cross.length > 1) {
      total += scoreWord(cross);
    }
  }

  // Bingo (+50) for using all 7 tiles
  if (placements.length === 7) total += 50;

  return { ok:true, score: total };
}


function shuffleArray(a) {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** ---------- Main component ---------- */

export default function Babble() {
  const { user } = useAuth();
  const { id: gameIdParam } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [me, setMe] = useState(null);            // "host" | "guest" | "spectator"
  const [status, setStatus] = useState(null);

  // move-local UI state
  const [placements, setPlacements] = useState([]); // [{r,c,letter,rackIndex}]
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");

  // local rack ordering (private; not saved to server)
  const [rackOrder, setRackOrder] = useState([]); // displayIndex -> myRack index
  const [celebration, setCelebration] = useState(null);
  const lastWinKeyRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1440));
  const {
    playTilePlace,
    playTileLift,
    playShuffle,
    playConfirm,
    playSoftError
  } = useSoftSounds();

  // NEW: control when we auto-create
  const creatingOnce = useRef(false);
  const shouldAutoCreate = gameIdParam === "new";
  const gameId = shouldAutoCreate ? null : (gameIdParam || null);

  // create or subscribe
  useEffect(() => {
    if (!user) return;
    let unsub = null;

    (async () => {
      try {
        if (shouldAutoCreate) {
          // CREATE ONLY on /babble/new
          if (creatingOnce.current) return; // guard double-render
          creatingOnce.current = true;

          const boardRows = newBoardRows();
          let { bag, rack: hostRack } = drawTiles(BAG, [], 7);
          let guestRack = [];
          ({ bag, rack: guestRack } = drawTiles(bag, [], 7));

          const payload = {
            createdAt: serverTimestamp(),
            hostUid: user.uid,
            guestUid: "",
            currentTurnUid: user.uid,
            scores: { host: 0, guest: 0 },
            boardRows, bag,
            racks: { host: hostRack, guest: guestRack },
            scorelessTurns: 0,
            status: "waiting",
            title: "Babble"
          };

          const ref = await addDoc(collection(db, "games_babble"), payload);
          navigate(`/babble/${ref.id}`, { replace: true });
          return;
        }

        if (!gameId) {
          // Not creating and no id → do nothing
          setStatus("No game selected. Go to /babble/new to start one.");
          return;
        }

        // SUBSCRIBE
        const ref = doc(db, "games_babble", gameId);
        unsub = onSnapshot(
          ref,
          async (snap) => {
            if (!snap.exists()) { setStatus("Game not found."); return; }
            const data = snap.data();
            setGame({ id: snap.id, ...data });

            if (data.hostUid === user.uid) setMe("host");
            else if (data.guestUid === user.uid) setMe("guest");
            else if (!data.guestUid) {
              try {
                await updateDoc(ref, { guestUid: user.uid, status: "live", updatedAt: serverTimestamp() });
                setMe("guest");
              } catch (e) {
                console.error("[Babble] join", e);
                setStatus(`Join failed: ${e.code || ""} ${e.message || e}`);
              }
            } else {
              setMe("spectator");
              setStatus("This game already has two players (spectating).");
            }
          },
          (err) => { console.error("[Babble] subscribe", err); setStatus(`${err.code || "error"} — ${err.message}`); }
        );
      } catch (e) {
        console.error("[Babble] setup", e);
        setStatus(`Setup failed: ${e.code || "error"} — ${e.message}`);
      }
    })();

    return () => unsub && unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, gameId, shouldAutoCreate, navigate]);

  // chat
  useEffect(() => {
    if (!game) return;
    const qy = query(collection(doc(db, "games_babble", game.id), "chat"), orderBy("at", "asc"));
    const unsub = onSnapshot(qy, s => setChat(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [game]);

  useEffect(() => {
    if (!game || game.status !== "finished" || !game.winnerUid || !user) return;
    const key = `${game.id}:${game.winnerUid}`;
    if (lastWinKeyRef.current === key) return;
    lastWinKeyRef.current = key;
    const isMe = game.winnerUid === user.uid;
    const winnerLabel = game.winnerUid === game.hostUid ? "Host" : "Guest";
    const endedOnScoreless = (game.scorelessTurns || 0) >= 6;
    setCelebration({
      type: "win",
      message: isMe ? "You won Babble!" : `${winnerLabel} wins!`,
      subtext: endedOnScoreless
        ? (isMe ? "You won after a scoreless stretch." : "Decided after six scoreless turns.")
        : (isMe ? "All tiles cleared 🎉" : "Better luck next time.")
    });
  }, [game, user]);

  // whenever my rack changes from server, reset/resize the local order
  const myRack = useMemo(() => (me && game ? game.racks[me] : []), [me, game]);
  useEffect(() => {
    if (!myRack) return;
    setRackOrder((prev) => {
      const base = prev.length ? prev.slice() : Array.from({length: myRack.length}, (_,i)=>i);
      const want = Array.from({length: myRack.length}, (_,i)=>i);
      for (const i of want) if (!base.includes(i)) base.push(i);
      return base.filter(i => i < myRack.length);
    });
  }, [myRack]);

  const oppRackCount = useMemo(() => {
    if (!game || !me) return 0;
    const other = me === "host" ? "guest" : "host";
    return (game.racks[other] || []).length;
  }, [game, me]);
  const isMyTurn = game && user && game.status !== "finished" && (game.currentTurnUid === user.uid);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handle = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  const boardCellSize = useMemo(() => {
    if (viewportWidth >= 1500) return 36;
    if (viewportWidth >= 1200) return 32;
    if (viewportWidth >= 960) return 28;
    return 24;
  }, [viewportWidth]);

  const [buddyMood, setBuddyMood] = useState("idle");
  const buddyBaseMoodRef = useRef("idle");
  const buddyDanceTimerRef = useRef(null);
  const baseBuddyMood = isMyTurn ? "idle" : "waiting";
  useEffect(() => {
    buddyBaseMoodRef.current = baseBuddyMood;
    if (buddyMood !== "dance") {
      setBuddyMood(baseBuddyMood);
    }
  }, [baseBuddyMood, buddyMood]);
  useEffect(() => () => {
    if (buddyDanceTimerRef.current) clearTimeout(buddyDanceTimerRef.current);
  }, []);
  const triggerBuddyDance = useCallback(() => {
    if (buddyDanceTimerRef.current) clearTimeout(buddyDanceTimerRef.current);
    setBuddyMood("dance");
    buddyDanceTimerRef.current = setTimeout(() => {
      setBuddyMood(buddyBaseMoodRef.current);
    }, 2400);
  }, []);

  /** ------ Helpers for displayed rack ------ */
  const displayedTiles = rackOrder.map(idx => myRack?.[idx]).filter(ch => ch != null);
  const usedRackIndices = new Set(placements.map(p => p.rackIndex));
  const isRackDisplayIndexUsed = (displayIdx) => {
    const trueIdx = rackOrder[displayIdx];
    return usedRackIndices.has(trueIdx);
  };

  const moveRackIndexToPosition = (rackIndex, targetDisplayPos) => {
    setRackOrder((ord) => {
      const oldPos = ord.indexOf(rackIndex);
      if (oldPos === -1) return ord;
      const next = ord.slice();
      next.splice(oldPos, 1);
      next.splice(Math.max(0, Math.min(targetDisplayPos, next.length)), 0, rackIndex);
      return next;
    });
  };

  const shuffleRack = () => {
    setRackOrder((ord) => shuffleArray(ord));
    playShuffle();
  };
  const clearPlacements = () => {
    if (placements.length === 0) return;
    setPlacements([]);
    playTileLift();
  };

  /** ------ Placement interactions ------ */

  const placeAt = (r, c, fromDisplayIndex = null) => {
    if (!isMyTurn || !game) return;
    const matrix = rowsToMatrix(game.boardRows);
    if (matrix[r][c] && matrix[r][c] !== ".") return; // occupied

    let displayIndex = fromDisplayIndex;
    if (displayIndex == null) return;

    const rackIndex = rackOrder[displayIndex];
    if (usedRackIndices.has(rackIndex)) return; // already used in this move
    const letter = myRack[rackIndex];

    let added = false;
    setPlacements(prev => {
      if (prev.some(p => p.r===r && p.c===c)) return prev;
      added = true;
      return [...prev, { r, c, letter, rackIndex }];
    });
    if (added) playTilePlace();
  };

  const unplaceAt = (r, c) => {
    let changed = false;
    setPlacements(prev => {
      const next = prev.filter(p => !(p.r===r && p.c===c));
      changed = changed || next.length !== prev.length;
      return next;
    });
    if (changed) playTileLift();
  };

  // HTML5 DnD: board (placed tile) → rack (unplace)
  const onPlacedDragStart = (e, placement) => {
    e.dataTransfer.setData("text/babble-type", "placement");
    e.dataTransfer.setData("text/babble-rack-index", String(placement.rackIndex));
    e.dataTransfer.setData("text/babble-r", String(placement.r));
    e.dataTransfer.setData("text/babble-c", String(placement.c));
    e.dataTransfer.effectAllowed = "move";
  };

  // Board drop targets
  const onCellDrop = (r, c, e) => {
    const type = e.dataTransfer.getData("text/babble-type");
    if (type !== "rack") return;
    const displayIndex = parseInt(e.dataTransfer.getData("text/babble-display-index") || "-1", 10);
    if (displayIndex >= 0) placeAt(r, c, displayIndex);
  };

  // Rack drop targets (reorder or take back a placed tile)
  const onRackDropAt = (targetDisplayPos, e) => {
    const type = e.dataTransfer.getData("text/babble-type");
    if (type === "rack") {
      const displayIndex = parseInt(e.dataTransfer.getData("text/babble-display-index") || "-1", 10);
      const rackIndex = rackOrder[displayIndex];
      if (rackIndex != null) moveRackIndexToPosition(rackIndex, targetDisplayPos);
    } else if (type === "placement") {
      const rackIndex = parseInt(e.dataTransfer.getData("text/babble-rack-index") || "-1", 10);
      const r = parseInt(e.dataTransfer.getData("text/babble-r") || "-1", 10);
      const c = parseInt(e.dataTransfer.getData("text/babble-c") || "-1", 10);
      if (rackIndex >= 0) {
        let removed = false;
        setPlacements(prev => {
          const next = prev.filter(p => !(p.r===r && p.c===c));
          removed = removed || next.length !== prev.length;
          return next;
        });
        moveRackIndexToPosition(rackIndex, targetDisplayPos);
        if (removed) playTileLift();
      }
    }
  };

  const commitMove = async () => {
    if (!game || !isMyTurn || placements.length === 0) return;

    // fast local validation to avoid noisy transactions
    const localCheck = scoreMove(rowsToMatrix(game.boardRows), placements);
    if (!localCheck.ok) { setStatus(localCheck.reason); playSoftError(); return; }

    const ref = doc(db, "games_babble", game.id);
    try {
      const result = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
          const err = new Error("Game not found.");
          err.code = "not-found";
          throw err;
        }
        const data = snap.data();
        const role = data.hostUid === user.uid ? "host" : (data.guestUid === user.uid ? "guest" : null);
        if (!role) {
          const err = new Error("You are not an active player in this game.");
          err.code = "not-player";
          throw err;
        }
        if (data.currentTurnUid !== user.uid) {
          const err = new Error("It's not your turn anymore.");
          err.code = "not-your-turn";
          throw err;
        }

        const boardRows = Array.isArray(data.boardRows) ? data.boardRows.slice() : newBoardRows();
        const serverCheck = scoreMove(rowsToMatrix(boardRows), placements);
        if (!serverCheck.ok) {
          const err = new Error(serverCheck.reason);
          err.code = "invalid-move";
          throw err;
        }

        let nextBoardRows = boardRows;
        placements.forEach(p => { nextBoardRows = setCell(nextBoardRows, p.r, p.c, p.letter); });

        const racks = { ...(data.racks || {}) };
        const myServerRack = Array.isArray(racks[role]) ? [...racks[role]] : [];
        const placedTrueIdxs = placements.map(p => p.rackIndex).sort((a,b)=>b-a);
        placedTrueIdxs.forEach(idx => myServerRack.splice(idx,1));
        const sourceBag = (typeof data.bag === "string") ? data.bag : BAG;
        const drawn = drawTiles(sourceBag, myServerRack, 7 - myServerRack.length);
        racks[role] = drawn.rack;
        const bag = drawn.bag;

        const scores = { ...(data.scores || {}) };
        const moveScore = serverCheck.score;
        scores[role] = (scores[role] || 0) + moveScore;

        const otherRole = role === "host" ? "guest" : "host";
        const otherUid = role === "host" ? data.guestUid : data.hostUid;

        let nextStatus = data.status;
        if (nextStatus === "waiting" && data.guestUid) nextStatus = "live";
        let winnerUid = data.winnerUid || "";
        let scorelessTurns = typeof data.scorelessTurns === "number" ? data.scorelessTurns : 0;
        scorelessTurns = moveScore > 0 ? 0 : scorelessTurns + 1;
        const clearedRack = racks[role].length === 0;
        const bagEmpty = bag.length === 0;
        let finished = false;
        if (bagEmpty && clearedRack) {
          finished = true;
          nextStatus = "finished";
          winnerUid = user.uid;
          const leftovers = rackPoints(racks[otherRole]);
          scores[role] = (scores[role] || 0) + leftovers;
          scores[otherRole] = (scores[otherRole] || 0) - leftovers;
          scorelessTurns = 0;
        } else if (scorelessTurns >= 6) {
          finished = true;
          nextStatus = "finished";
          scores.host = (scores.host || 0) - rackPoints(racks.host);
          scores.guest = (scores.guest || 0) - rackPoints(racks.guest);
          if (data.hostUid && data.guestUid) {
            if (scores.host > scores.guest) winnerUid = data.hostUid;
            else if (scores.guest > scores.host) winnerUid = data.guestUid;
            else winnerUid = "";
          } else {
            winnerUid = role === "host" ? data.hostUid : (data.guestUid || data.hostUid);
          }
        }

        const nextTurn = finished ? "" : (otherUid || user.uid);

        const payload = {
          boardRows: nextBoardRows,
          bag,
          racks,
          scores,
          currentTurnUid: nextTurn,
          updatedAt: serverTimestamp(),
          status: nextStatus,
          scorelessTurns
        };
        if (winnerUid) payload.winnerUid = winnerUid;
        tx.update(ref, payload);

        return {
          score: moveScore,
          rackLength: racks[role].length,
          bingo: placements.length === 7,
          finished,
          winnerUid
        };
      });

      setPlacements([]);
      const finishLabel = result.finished
        ? (result.winnerUid
            ? (result.winnerUid === user.uid ? "Game finished. You win!" : (result.winnerUid === game.hostUid ? "Game finished. Host wins." : "Game finished. Guest wins."))
            : "Game ended after six scoreless turns.")
        : `+${result.score} points`;
      setStatus(finishLabel);
      setRackOrder(Array.from({length: 7}, (_,i)=>i).slice(0, result.rackLength));
      playConfirm();
      triggerBuddyDance();
      if (result.bingo) {
        setCelebration({ type: "bingo", message: "Bingo!", subtext: "All 7 tiles played 💥" });
      }
      if (result.winnerUid && result.winnerUid === user.uid) {
        setCelebration({ type: "win", message: "You won Babble!", subtext: "All tiles cleared 🎉" });
      }
    } catch (e) {
      if (e.code === "invalid-move") {
        setStatus(e.message);
      } else if (e.code === "not-your-turn") {
        setStatus("It's not your turn anymore. Syncing the board…");
      } else if (e.code === "not-player") {
        setStatus("Only players in this game can make moves.");
      } else if (e.code === "not-found") {
        setStatus("Game not found.");
      } else {
        console.error("[Babble] commit", e);
        setStatus(`commit failed: ${e.code || "error"} — ${e.message}`);
      }
      playSoftError();
    }
  };

  const passTurn = async () => {
    if (!game || !isMyTurn) return;
    const ref = doc(db, "games_babble", game.id);
    try {
      const result = await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) {
          const err = new Error("Game not found.");
          err.code = "not-found";
          throw err;
        }
        const data = snap.data();
        const role = data.hostUid === user.uid ? "host" : (data.guestUid === user.uid ? "guest" : null);
        if (!role) {
          const err = new Error("You are not an active player in this game.");
          err.code = "not-player";
          throw err;
        }
        if (data.currentTurnUid !== user.uid) {
          const err = new Error("It's not your turn anymore.");
          err.code = "not-your-turn";
          throw err;
        }

        const scores = { ...(data.scores || {}) };
        const racks = { ...(data.racks || {}) };
        let scorelessTurns = typeof data.scorelessTurns === "number" ? data.scorelessTurns : 0;
        scorelessTurns += 1;

        let nextStatus = data.status;
        let winnerUid = data.winnerUid || "";
        let finished = false;
        if (scorelessTurns >= 6) {
          finished = true;
          nextStatus = "finished";
          scores.host = (scores.host || 0) - rackPoints(racks.host);
          scores.guest = (scores.guest || 0) - rackPoints(racks.guest);
          if (data.hostUid && data.guestUid) {
            if (scores.host > scores.guest) winnerUid = data.hostUid;
            else if (scores.guest > scores.host) winnerUid = data.guestUid;
            else winnerUid = "";
          } else {
            winnerUid = data.hostUid || data.guestUid || "";
          }
        }

        const otherUid = role === "host" ? data.guestUid : data.hostUid;
        const nextTurn = finished ? "" : (otherUid || user.uid);

        const payload = {
          currentTurnUid: nextTurn,
          updatedAt: serverTimestamp(),
          scorelessTurns
        };
        if (finished) {
          payload.status = nextStatus;
          payload.scores = scores;
          if (winnerUid) payload.winnerUid = winnerUid;
        }
        tx.update(ref, payload);
        return { finished };
      });

      if (result.finished) {
        setStatus("Game ended after consecutive scoreless turns.");
      }
    } catch (e) {
      if (e.code === "not-your-turn") {
        setStatus("It's not your turn anymore. Syncing the board…");
      } else if (e.code === "not-player") {
        setStatus("Only players in this game can make moves.");
      } else if (e.code === "not-found") {
        setStatus("Game not found.");
      } else {
        console.error("[Babble] pass", e);
        setStatus(`Pass failed: ${e.code || "error"} — ${e.message}`);
      }
      playSoftError();
      return;
    }
    playTileLift();
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!game || !msg.trim()) return;
    await addDoc(collection(doc(db, "games_babble", game.id), "chat"), {
      text: msg.trim(), at: serverTimestamp(), senderUid: user.uid
    });
    setMsg("");
  };

  /** ---------- Highlight main line ---------- */
  const highlight = useMemo(() => {
    if (placements.length === 0) return null;
    const rs = new Set(placements.map(p => p.r));
    const cs = new Set(placements.map(p => p.c));
    if (rs.size === 1) return { row: placements[0].r };
    if (cs.size === 1) return { col: placements[0].c };
    return null;
  }, [placements]);

  /** ---------- Render ---------- */

  if (!user) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!game && !shouldAutoCreate) {
    return (
      <div style={{ maxWidth: 960, margin: "24px auto", padding: "0 16px" }}>
      <header style={{ marginBottom: 28 }}>
        <h1 style={{ margin: "0 0 10px" }} aria-label="Babble">
          <BabbleWordmark aria-hidden="true" />
        </h1>
        <p style={{ margin: 0, fontSize: 15, opacity: 0.85 }}>
          Choose one of your current games below or <Link to="/babble/new">deal a fresh board</Link> to invite a friend.
        </p>
      </header>
        {status && (
          <div style={{ background: "#fff8e1", border: "1px solid #ffd54f", padding: 12, borderRadius: 10, marginBottom: 16 }}>
            {status}
          </div>
        )}

        <MyBabbleGames
          title="My Babble games"
          layout="grid"
          style={{ marginTop: 12 }}
        />

        <div style={{ marginTop: 32, border: "1px dashed #c5cae9", borderRadius: 12, padding: 16, background: "#f9f7ff" }}>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: "#303f9f" }}>
            Tip: share the link from any game card to let a friend jump right in, or bookmark <code>/babble/&lt;gameId&gt;</code> to keep your turn handy.
          </div>
        </div>
      </div>
    );
  }
  if (!game && shouldAutoCreate) return <div style={{ padding: 24 }}>Setting up your game…</div>;

  return (
    <div style={{ maxWidth: 1100, margin: "24px auto", padding: "0 16px" }}>
      {celebration && (
        <CelebrationOverlay
          message={celebration.message}
          subtext={celebration.subtext}
          onClose={() => setCelebration(null)}
        />
      )}
      <header style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <h1 style={{ margin: 0 }} aria-label="Babble">
          <BabbleWordmark aria-hidden="true" />
        </h1>
        <div style={{
          fontSize:14,
          background:"linear-gradient(120deg, rgba(148,187,233,0.18), rgba(238,174,202,0.18))",
          border:"1px solid rgba(148,187,233,0.4)",
          borderRadius:12,
          padding:"10px 14px",
          boxShadow:"inset 0 1px 0 rgba(255,255,255,0.4)",
          display:"flex",
          flexDirection:"column",
          gap:4,
          minWidth:280
        }}>
          <div style={{ textTransform:"uppercase", letterSpacing:1, fontSize:11, opacity:0.8 }}>
            Game card
          </div>
          <div>
            <strong style={{ fontSize:13, letterSpacing:0.4 }}>ID:</strong>{" "}
            <code style={{ background:"#fff", padding:"2px 4px", borderRadius:6 }}>{game.id}</code>
          </div>
          <div style={{ fontSize:13, display:"flex", gap:6, flexWrap:"wrap" }}>
            <span style={{ fontWeight:600 }}>Share:</span>
            <code style={{ background:"#fff", padding:"2px 4px", borderRadius:6 }}>{window.location.href}</code>
          </div>
        </div>
      </header>

      {status && !gameIdParam && <div style={{ background:"#eef9ee", border:"1px solid #8bc34a", padding:8, borderRadius:8, margin:"12px 0" }}>{status}</div>}

      {game.status === "finished" && (
        <div style={{ marginBottom: 12, padding: 12, borderRadius: 10, border: "1px solid #ffb74d", background: "#fff3e0" }}>
          <strong>Game finished.</strong>{" "}
          {game.winnerUid
            ? (game.winnerUid === user.uid ? "You win!" : (game.winnerUid === game.hostUid ? "Host wins." : "Guest wins."))
            : "Ended after six scoreless turns."}
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"minmax(520px,1fr) 340px", gap:20 }}>
        {/* Board */}
        <div>
          <div style={{ ...BOARD_FRAME_STYLE }}>
            <div style={{
              position: "absolute",
              top: 12,
              left: 24,
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 1.6,
              color: "rgba(0,0,0,0.6)",
              background: "rgba(255,255,255,0.75)",
              padding: "4px 12px",
              borderRadius: 999,
              border: "1px solid rgba(0,0,0,0.08)"
            }}>
              Selected game board
            </div>
            <div style={BOARD_SURFACE_STYLE}>
              <div style={BOARD_WRAPPER_STYLE}>
                <Board
                  boardRows={game.boardRows}
                  placements={placements}
                  highlight={highlight}
                  undoPlacement={unplaceAt}
                  onCellDragOver={(e, allow)=>{ if(allow){ e.preventDefault(); e.dataTransfer.dropEffect='move'; } }}
                  onCellDrop={onCellDrop}
                  onPlacedDragStart={onPlacedDragStart}
                  cellSize={boardCellSize}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: 12, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <button disabled={!isMyTurn || placements.length===0} onClick={commitMove}>Commit move</button>
            <button disabled={placements.length===0} onClick={clearPlacements}>Clear</button>
            <button disabled={!isMyTurn} onClick={passTurn}>Pass</button>
            <span style={{marginLeft:12, fontSize:13, opacity:.8}}>
              {isMyTurn ? "Your turn" : "Waiting for opponent…"}
            </span>
          </div>
        </div>

        {/* Side */}
        <div>
          <h3>Rack</h3>
          <Rack
            tiles={displayedTiles}
            rackOrder={rackOrder}
            isUsedDisplayIndex={isRackDisplayIndexUsed}
            onDragStartTile={(e, idx)=>{ e.dataTransfer.setData("text/babble-type","rack"); e.dataTransfer.setData("text/babble-display-index", String(idx)); e.dataTransfer.effectAllowed="move"; }}
            onDragOver={(e)=>{ e.preventDefault(); e.dataTransfer.dropEffect="move"; }}
            onDropAt={onRackDropAt}
            onShuffle={shuffleRack}
          />
          <div style={{ marginTop: 12, fontSize:13, opacity:.8 }}>
            <span style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#fdf3e8", padding:"6px 10px", borderRadius:12, border:"1px solid #f0d9b5" }}>
              Opponent tiles: <strong>{oppRackCount}</strong> • Bag: <strong>{game.bag.length}</strong>
            </span>
          </div>

          <div style={{ marginTop:16 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
              <h3 style={{ margin: 0 }}>Scores</h3>
              <BabbleTileBuddy mood={buddyMood} />
            </div>
            <div style={{ fontSize: 16, marginTop: 8 }}>
              You: <b>{game.scores[me] || 0}</b><br/>
              Opponent: <b>{game.scores[me==="host" ? "guest" : "host"] || 0}</b>
            </div>
          </div>

          <h3 style={{marginTop:16}}>Chat</h3>
          <div style={{ border:"1px solid #ddd", borderRadius:8, padding:"10px 8px", height:180, overflow:"auto", background:"#fafafa", display:"flex", flexDirection:"column", gap:10 }}>
            {chat.map(m => {
              const mine = m.senderUid === user.uid;
              const label = mine ? "You" : "Them";
              const bubbleColor = mine ? "#e3f2fd" : "#fff";
              const bubbleBorder = mine ? "#bbdefb" : "#e0e0e0";
              const bubbleRadius = mine ? "16px 16px 0 16px" : "16px 16px 16px 0";
              const tailStyles = mine
                ? { right: -6, borderLeftColor: bubbleBorder, borderLeftWidth: 6, borderRightWidth: 0 }
                : { left: -6, borderRightColor: bubbleBorder, borderRightWidth: 6, borderLeftWidth: 0 };
              const tailFill = mine
                ? { right: -4, borderLeftColor: bubbleColor, borderLeftWidth: 5, borderRightWidth: 0 }
                : { left: -4, borderRightColor: bubbleColor, borderRightWidth: 5, borderLeftWidth: 0 };
              return (
                <div key={m.id} style={{ display:"flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                  <div style={{
                    background: bubbleColor,
                    border: `1px solid ${bubbleBorder}`,
                    padding: "6px 10px 8px",
                    borderRadius: bubbleRadius,
                    maxWidth: "82%",
                    fontSize: 14,
                    position: "relative",
                    boxShadow: "0 1px 2px rgba(0,0,0,.08)"
                  }}>
                    <div style={{ fontSize:11, letterSpacing:0.4, textTransform:"uppercase", opacity:0.6, marginBottom:2, textAlign: mine ? "right" : "left" }}>
                      {label}
                    </div>
                    <div style={{ whiteSpace:"pre-wrap" }}>
                      {m.text}
                    </div>
                    <span style={{
                      position:"absolute",
                      bottom:0,
                      width:0,
                      height:0,
                      borderTop: "6px solid transparent",
                      borderBottom: "6px solid transparent",
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      ...tailStyles
                    }} />
                    <span style={{
                      position:"absolute",
                      bottom:1,
                      width:0,
                      height:0,
                      borderTop: "5px solid transparent",
                      borderBottom: "5px solid transparent",
                      borderLeft: "5px solid transparent",
                      borderRight: "5px solid transparent",
                      ...tailFill
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={sendMsg} style={{ display:"flex", gap:8, marginTop:8 }}>
            <input value={msg} onChange={(e)=>setMsg(e.target.value)} placeholder="Type a message…" style={{ flex:1, padding:8 }} />
            <button>Send</button>
          </form>

          <MyBabbleGames
            title="Manage my games"
            headingLevel={3}
            layout="stack"
            style={{ marginTop: 24 }}
          />
        </div>
      </div>
    </div>
  );
}

/** ---------- UI bits ---------- */

function Board({ boardRows, placements, highlight, undoPlacement, onCellDragOver, onCellDrop, onPlacedDragStart, cellSize=36 }) {
  const placedMap = useMemo(() => {
    const m = new Map();
    placements.forEach(p => m.set(`${p.r},${p.c}`, p));
    return m;
  }, [placements]);

  const matrix = rowsToMatrix(boardRows);
  const gapPx = Math.max(1, Math.round(cellSize * 0.05));
  const padPx = Math.max(2, Math.round(cellSize * 0.05));
  const boardWidth = (cellSize * SIZE) + gapPx * (SIZE - 1) + padPx * 2;

  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:`repeat(${SIZE}, ${cellSize}px)`,
      gridAutoRows:`${cellSize}px`,
      gap:gapPx,
      background:"#ccc",
      padding:padPx,
      borderRadius:8,
      width: boardWidth
    }}>
      {matrix.map((row, r) => row.map((cell, c) => {
        const key = `${r},${c}`;
        const placed = placedMap.get(key);
        const hasPlaced = !!placed;
        const letter = hasPlaced ? placed.letter : (cell === "." ? "" : cell);
        const bonus = BONUS[r][c];
        const occupied = !!letter;
        const bg = occupied ? "#fff" :
          bonus==="TW" ? "#ffebee" :
          bonus==="DW" ? "#e8f5e9" :
          bonus==="TL" ? "#e3f2fd" :
          bonus==="DL" ? "#fff3e0" : "#f5f5f5";

        const onDragOver = (e) => onCellDragOver(e, !occupied);
        const onDrop = (e) => onCellDrop(r, c, e);

        const isHighlight = highlight?.row === r || highlight?.col === c;

        return (
          <div
            key={key}
            onClick={() => (hasPlaced ? undoPlacement(r,c) : null)}
            onDragOver={onDragOver}
            onDrop={onDrop}
            title={bonus ? BONUS_LABEL[bonus] : ""}
            style={{
              width:36, height:36, display:"grid", placeItems:"center",
              background:bg, borderRadius:4, userSelect:"none",
              border: hasPlaced ? "2px solid #4caf50" : (isHighlight ? "2px dashed #90caf9" : "1px solid #ddd"),
              position:"relative"
            }}
          >
            {letter ? (
              <Tile
                ch={letter}
                draggable={hasPlaced}
                variant="wood"
                onDragStart={hasPlaced ? (e)=>{ e.dataTransfer.setData("text/babble-type","placement"); e.dataTransfer.setData("text/babble-rack-index", String(placed.rackIndex)); e.dataTransfer.setData("text/babble-r", String(placed.r)); e.dataTransfer.setData("text/babble-c", String(placed.c)); e.dataTransfer.effectAllowed="move"; } : undefined}
              />
            ) : (
              bonus && (
                <span style={{
                  fontSize: 9, fontWeight: 800, color: "#555",
                  position: "absolute", inset: 0,
                  display: "grid", placeItems: "center",
                  textAlign: "center", lineHeight: 1.1, padding: 2, opacity: .85
                }}>
                  {bonus}
                </span>
              )
            )}
          </div>
        );
      }))}
    </div>
  );
}

const BABBLE_WORD = ["B","A","B","B","L","E"];

function Tile({ ch, draggable=false, onDragStart, size=32, variant="classic" }) {
  const letterFont = Math.round(size * 0.45);
  const pointFont = Math.round(size * 0.28);
  const radius = Math.max(6, Math.round(size * 0.2));
  const shadowY = Math.max(1, size * 0.04);
  const isWood = variant === "wood";
  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      style={{
        width:size, height:size, display:"grid", placeItems:"center",
        background: isWood ? "linear-gradient(135deg, #fbe3c5, #dfa665)" : "#fff",
        border: isWood ? "1px solid #c37b32" : "1px solid #bbb",
        borderRadius:radius,
        fontWeight:800, position:"relative",
        boxShadow: isWood ? `0 ${shadowY}px ${shadowY*2}px rgba(92,52,15,.25)` : `0 ${shadowY}px ${shadowY*2}px rgba(0,0,0,.2)`,
        fontSize: letterFont,
        color: isWood ? "#4f2b07" : "inherit",
        textShadow: isWood ? "0 1px 0 rgba(255,255,255,0.45)" : "none",
      }}
      title={`${ch} (${POINTS[ch]||0})`}
    >
      {ch}
      <span style={{ position:"absolute", right:4, bottom:2, fontSize:pointFont, opacity: isWood ? .9 : .75 }}>
        {POINTS[ch] || 0}
      </span>
    </div>
  );
}

function BabbleWordmark({ size=44, gap=6, style={}, ...rest }) {
  const rackStyle = {
    display:"flex",
    alignItems:"flex-start",
    gap,
    flexWrap:"nowrap",
    padding:"8px 14px 18px",
    background:"linear-gradient(180deg, rgba(255,255,255,0.9), rgba(238,214,187,0.9))",
    border:"1px solid rgba(195,150,96,0.4)",
    borderRadius:18,
    boxShadow:"inset 0 1px 0 rgba(255,255,255,0.7), 0 3px 8px rgba(0,0,0,0.12)",
    position:"relative"
  };
  const baseStyle = {
    position:"absolute",
    left:12,
    right:12,
    bottom:6,
    height:8,
    borderRadius:999,
    background:"linear-gradient(90deg, rgba(211,150,84,0.8), rgba(255,193,120,0.8))",
    boxShadow:"0 2px 4px rgba(0,0,0,0.18)"
  };
  return (
    <div {...rest} style={{ display:"inline-flex", ...style }}>
      <div style={rackStyle}>
        {BABBLE_WORD.map((letter, idx) => (
          <Tile key={`${letter}-${idx}`} ch={letter} size={size} variant="wood" />
        ))}
        <div style={baseStyle} aria-hidden="true" />
      </div>
    </div>
  );
}

function Rack({
  tiles, rackOrder, isUsedDisplayIndex,
  onDragStartTile, onDragOver, onDropAt, onShuffle
}) {
  // Render N drop zones: before each tile + one at the end
  return (
    <div>
      <div style={{ display:"flex", gap:12, alignItems:"flex-start", flexWrap:"wrap" }}>
        <button
          onClick={onShuffle}
          style={{
            padding:"8px 14px",
            borderRadius:20,
            background:"linear-gradient(120deg, #ffe29f, #ffa99f)",
            border:"1px solid rgba(255,169,159,0.6)",
            boxShadow:"0 2px 4px rgba(0,0,0,.1)",
            fontWeight:600,
            letterSpacing:0.5
          }}
        >
          Shuffle
        </button>
        <div style={{
          position:"relative",
          padding:"12px 16px 26px",
          borderRadius:20,
          border:"1px solid rgba(141,110,63,0.4)",
          background:"linear-gradient(180deg, rgba(255,255,255,0.95), rgba(241,224,199,0.95))",
          boxShadow:"inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 8px rgba(0,0,0,0.1)",
          flex:"1 1 auto",
          minWidth:220,
          overflow:"hidden"
        }}>
          <div style={{ display:"flex", gap:4, flexWrap:"nowrap", overflowX:"hidden" }}>
            {tiles.map((ch, displayIdx) => (
              <React.Fragment key={`tile-${displayIdx}`}>
                <RackDropSlot
                  index={displayIdx}
                  onDragOver={onDragOver}
                  onDropAt={onDropAt}
                />
                <button
                  draggable={!isUsedDisplayIndex(displayIdx)}
                  onDragStart={(e) => onDragStartTile(e, displayIdx)}
                  style={{
                    width:38, height:38, padding:0,
                    border: isUsedDisplayIndex(displayIdx) ? "2px solid #4caf50" : "1px solid #ccc",
                    borderRadius:6, background:"transparent", cursor: isUsedDisplayIndex(displayIdx) ? "not-allowed" : "grab"
                  }}
                  title={`${ch} (${POINTS[ch]||0})`}
                >
                  <Tile ch={ch} variant="wood" />
                </button>
              </React.Fragment>
            ))}
            <RackDropSlot
              index={tiles.length}
              onDragOver={onDragOver}
              onDropAt={onDropAt}
            />
          </div>
          <div style={{
            position:"absolute",
            left:12,
            right:12,
            bottom:6,
            height:8,
            borderRadius:999,
            background:"linear-gradient(90deg, rgba(196,140,80,0.85), rgba(255,201,135,0.85))",
            boxShadow:"0 2px 4px rgba(0,0,0,0.18)"
          }} />
        </div>
      </div>
      <div style={{ fontSize: 12, opacity:.7, marginTop: 6 }}>
        Tip: drag tiles to reorder, or drag a placed tile back here to unplace it.
      </div>
    </div>
  );
}

function RackDropSlot({ index, onDragOver, onDropAt }) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={(e)=>onDropAt(index, e)}
      style={{
        width: 10, height: 40, borderRadius: 2,
        background:"transparent"
      }}
    />
  );
}
